import { styled } from '@mui/material';

import { EmailAddressIcon, PhoneIcon } from '../../../icons';
import { Avatar } from '../../atoms/Avatar';
import { Typography } from '../../atoms/Typography';
import { Card } from '../Card';
import { type ContactCardProps, type ContactChannel, useContactCard } from './useContactCard';

type Props = ContactCardProps;

const Root = styled(Card)({
  height: '100%',
});

const Header = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  padding: theme.spacing(2),
  paddingBottom: theme.spacing(1),
}));

const Identity = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
});

const Name = styled('p')(({ theme }) => ({
  ...theme.typography.h6,
  margin: 0,
  color: theme.palette.text.primary,
  overflowWrap: 'anywhere',
}));

const Role = styled('span')(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
  overflowWrap: 'anywhere',
}));

const Action = styled('div')(({ theme }) => ({
  flexShrink: 0,
  marginTop: theme.spacing(-0.5),
  marginRight: theme.spacing(-1),
}));

const Channels = styled('ul')(({ theme }) => ({
  listStyle: 'none',
  margin: 0,
  padding: theme.spacing(0, 1, 1),
}));

const ChannelLink = styled('a')(({ theme }) => ({
  ...theme.typography.body2,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  minHeight: theme.spacing(5.5),
  paddingInline: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.text.primary,
  textDecoration: 'none',
  fontVariantNumeric: 'tabular-nums',
  transition: theme.transitions.create('background-color', {
    duration: theme.transitions.duration.shortest,
  }),
  '& > svg': {
    color: theme.palette.text.secondary,
  },
  '@media (hover: hover)': {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.primary.main,
    },
    '&:hover > svg': {
      color: theme.palette.primary.main,
    },
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
}));

const ChannelValue = styled('span')({
  minWidth: 0,
  overflowWrap: 'anywhere',
});

const NoChannel = styled(Typography)(({ theme }) => ({
  paddingInline: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  textAlign: 'left',
}));

function renderChannelIcon(channel: ContactChannel) {
  return channel.kind === 'phone'
    ? <PhoneIcon fontSize="small" aria-hidden="true" />
    : <EmailAddressIcon fontSize="small" aria-hidden="true" />;
}

/**
 * A provider's contact person. Phone numbers and e-mail are direct `tel:` / `mailto:` links
 * with comfortable touch targets, so a contact can be reached in one tap on site.
 */
export function ContactCard(props: Props) {
  const hook = useContactCard(props);

  return (
    <Root data-testid={props['data-testid'] ?? 'contact-card'}>
      <Header>
        <Avatar aria-hidden="true">{hook.initials}</Avatar>
        <Identity>
          <Name>{props.name}</Name>
          {props.role ? <Role>{props.role}</Role> : null}
        </Identity>
        {props.action ? <Action>{props.action}</Action> : null}
      </Header>

      {hook.hasChannels ? (
        <Channels aria-label={`Coordonnées de ${props.name}`}>
          {hook.channels.map((channel) => (
            <li key={channel.href}>
              <ChannelLink href={channel.href} aria-label={channel.label}>
                {renderChannelIcon(channel)}
                <ChannelValue>{channel.value}</ChannelValue>
              </ChannelLink>
            </li>
          ))}
        </Channels>
      ) : (
        <NoChannel variant="noItem">Aucune coordonnée renseignée</NoChannel>
      )}
    </Root>
  );
}

export type { ContactCardProps, ContactChannel };
